<template>
  <div class="c-header" :class="{ 'c-header--out-layout': isOutLayout }">
    <div class="c-header-left">
      <div v-if="showBack" class="c-header-back" @click="handleBack">
        <van-icon name="arrow-left" class="c-header-icon" />
      </div>
      <div class="c-header-titles">
        <slot name="title">
          <div class="c-header-main" :class="{ 'c-header-main--large': !hasSubtitle }">
            <span class="c-header-title-text">{{ displayTitle }}</span>
            <!-- 状态标签 -->
            <span v-if="showStatusBadge || computedShowStatus" class="c-header-status" :class="statusClass">
              <span v-if="computedStatusType === 'live' || computedStatusType === 'completed'"
                class="c-header-status-dot"
                :class="{ 'c-header-status-dot--live': computedStatusType === 'live', 'c-header-status-dot--completed': computedStatusType === 'completed' }"></span>
              {{ displayStatusText }}
            </span>
          </div>
          <div v-if="hasSubtitle" class="c-header-sub">
            <slot name="subtitle">
              {{ subtitle }}
            </slot>
          </div>
        </slot>
      </div>
    </div>

    <div class="c-header-right">
      <slot name="right">
        <div v-if="showPts" class="c-header-pts" @click="handlePtsClick">
          <SvgIcon name="pts" width="18px" color class="c-header-pts-icon" />
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { MatchStatusMap } from '@/views/contests/matchesType.js'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  showBack: {
    type: Boolean,
    default: true,
  },
  showPts: {
    type: Boolean,
    default: false,
  },
  ptsUrl: {
    type: String,
    default: '/rewards',
  },
  isOutLayout: {
    type: Boolean,
    default: false,
  },
  // 新增：状态类型
  statusType: {
    type: String,
    default: '', // 'live' | 'completed' | 'scheduled' | ''
  },
  // 新增：是否显示状态标签
  showStatusBadge: {
    type: Boolean,
    default: false,
  },
  // 新增：自定义状态文本
  statusText: {
    type: String,
    default: '',
  },
  // 新增：比赛状态码（用于自动映射文本）
  matchStatus: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['back'])

const route = useRoute()
const router = useRouter()

const displayTitle = computed(() => {
  return props.title || route.meta?.title || ''
})

const slots = useSlots()

const hasSubtitle = computed(() => {
  return !!props.subtitle || !!slots.subtitle
})
const showPts = computed(() => {
  return props.showPts || route.meta?.showPts || false
})
// 计算是否显示状态标签
const computedShowStatus = computed(() => {
  return props.showStatusBadge || !!props.statusType || props.matchStatus > 0
})

// 计算状态类型
const computedStatusType = computed(() => {
  if (props.statusType) return props.statusType
  // 根据matchStatus自动判断
  switch (props.matchStatus) {
    case 1:
      return 'scheduled'
    case 2:
      return 'completed'
    case 3:
      return 'live'
    default:
      return ''
  }
})

// 计算状态样式类名
const statusClass = computed(() => {
  return {
    'c-header-status--live': computedStatusType.value === 'live',
    'c-header-status--completed': computedStatusType.value === 'completed',
    'c-header-status--scheduled': computedStatusType.value === 'scheduled',
  }
})

// 计算显示的状态文本
const displayStatusText = computed(() => {
  if (props.statusText) return props.statusText
  // 根据matchStatus自动映射
  if (props.matchStatus > 0) {
    return MatchStatusMap[props.matchStatus] || ''
  }
  // 根据statusType返回默认文本
  switch (computedStatusType.value) {
    case 'live':
      return 'Live'
    case 'completed':
      return 'Completed'
    case 'scheduled':
      return 'Scheduled'
    default:
      return ''
  }
})

const handleBack = () => {
  emit('back')
  if (window.history.length > 1) {
    router.back()
  } else {
    router.replace({ name: 'Home' })
  }
}

const handlePtsClick = () => {
  router.push(props.ptsUrl)
}
</script>

<style scoped lang="less">
.c-header {
  &--out-layout {
    .c-header-left {
      margin-left: 10px;
    }
  }

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-image: url('@/assets/images/top_bg-DEOLogib.webp');

  &-left {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  &-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 4px;
    cursor: pointer;
  }

  &-icon {
    font-size: 20px;
    color: #ffffff;
  }

  &-titles {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    flex: 1;
  }

  &-main {
    font-size: 14px;
    color: #ffffff;
    font-family: CadillacTrimV1-SemiBold, sans-serif;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 6px;

    &--large {
      font-size: 16px;
      font-weight: 600;
    }
  }

  &-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // 状态标签样式
  &-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    font-family: CadillacTrimV1-SemiBold, sans-serif;
    font-weight: 600;
    border-radius: 10px;
    text-transform: uppercase;

    // Live状态 - 红色闪烁效果
    &--live {
      background: rgba(216, 45, 44, 0.2);
      color: #D82D2C;
      border: 1px solid rgba(216, 45, 44, 0.4);
      animation: live-pulse 2s ease-in-out infinite;
    }

    // Completed状态 - 灰色
    &--completed {
      background: rgba(106, 214, 128, 0.25);
      color: rgb(106, 214, 128);
      border: 1px solid rgba(136, 136, 136, 0.4);
    }

    // Scheduled状态 - 蓝色
    &--scheduled {
      background: rgba(0, 123, 255, 0.2);
      color: #007BFF;
      border: 1px solid rgba(0, 123, 255, 0.4);
      display: none;
    }
  }

  // Live状态闪烁点
  &-status-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;

    &--live {
      background: #D82D2C;
      animation: dot-blink 1s ease-in-out infinite;

    }

    &--completed {
      background: rgb(106, 214, 128);
    }
  }

  &-sub {
    font-size: 12px;
    color: #888;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 40px;
    height: 100%;
  }

  &-pts {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 4px;

    &-icon {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }
}

// Live状态闪烁动画
@keyframes live-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

@keyframes dot-blink {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}
</style>
